/* PatientClinicChat — patient ↔ clinic 1:1 chat · iter 98.14 redesign.
 *
 * Translated from the user-supplied "Chat with Doctor" HTML mockup,
 * with the Apollo cyan-teal palette retained.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────────┐
 *   │ Top bar — doctor avatar · name · online · "Replies in 4 hrs" │
 *   ├──────────────────────────────────────────┬───────────────────┤
 *   │                                          │  Doctor card      │
 *   │  Message thread (date dividers + left/   │  Response time    │
 *   │   right bubbles + read ticks + photos)   │  Next appointment │
 *   │                                          │  Treatment phase  │
 *   ├──────────────────────────────────────────┤  Recent photos    │
 *   │  Composer — quick-replies + textarea +   │  Today's wear     │
 *   │   attach + send                          │  Quick actions    │
 *   └──────────────────────────────────────────┴───────────────────┘
 *
 * Backend contract preserved (no changes):
 *   GET  /patient/clinic-chat/thread          — clinic_doctor + msgs
 *   POST /patients/{id}/messages               — send text + attachments
 *   POST /patients/{id}/chat-upload            — multipart attach
 *   POST /patients/{id}/messages/mark-read     — mark-read on load
 *   GET  /patient-chat-files/{id}              — download (blob)
 *
 * Context panel pulls:
 *   GET  /appointments/my                      — next appt card
 *   GET  /patient/me/aligner-progress          — phase + wear
 *   GET  /cases/mine                           — active case id
 *   GET  /patient/me/case/{id}/photos          — recent photos strip
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    ChatsCircle, PaperPlaneTilt, Spinner, Paperclip, X, FileText,
    Image as ImageIcon, Check, Checks, Buildings, Camera,
    CalendarBlank, Clock, Lock, MagnifyingGlass, CalendarPlus,
    Tooth, Plus, Lightning, ArrowsClockwise,
} from "@phosphor-icons/react";
import DoctorAvatar from "@/components/common/DoctorAvatar";


const POLL_MS = 30_000;
const FAST_POLL_MS = 5_000;


// ────────────────────────────────────────────────────────────────────
// Time + size formatters
// ────────────────────────────────────────────────────────────────────
function fmtTime(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return "";
    return t.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
}

function fmtDateDivider(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return "";
    const now = new Date();
    const todayStr = now.toDateString();
    const yStr = new Date(now.getTime() - 86400000).toDateString();
    const tStr = t.toDateString();
    const long = t.toLocaleDateString("en-IN", {
        weekday: "long", day: "2-digit", month: "short", year: "numeric",
    });
    if (tStr === todayStr) return `Today — ${long}`;
    if (tStr === yStr) return `Yesterday — ${long}`;
    return long;
}

function dayKey(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    return Number.isNaN(t.getTime()) ? "" : t.toISOString().slice(0, 10);
}

function fmtSize(bytes) {
    if (!bytes || Number.isNaN(bytes)) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const initialsOf = (name) =>
    (name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";


// ────────────────────────────────────────────────────────────────────
// Quick reply suggestions — patient-friendly canned starters.
// Hidden after the first send.
// ────────────────────────────────────────────────────────────────────
const QUICK_REPLIES = [
    "Thanks Dr. Wilson 😊",
    "I have a question about my retainer.",
    "My retainer feels uncomfortable — can we check this?",
    "Can I reschedule my appointment?",
    "I forgot to wear my retainer last night.",
];


// ────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────
export default function PatientClinicChat() {
    const navigate = useNavigate();
    const [clinic, setClinic] = useState(null);
    const [thread, setThread] = useState([]);
    const [loading, setLoading] = useState(true);
    const [patientId, setPatientId] = useState(null);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [showQuickReplies, setShowQuickReplies] = useState(true);
    const scrollRef = useRef(null);
    const pollTimerRef = useRef(null);
    const fileInputRef = useRef(null);

    /* Patient endpoint resolves clinic_doctor + thread + unread in one
       roundtrip. Polling re-pulls every POLL_MS while the tab is open. */
    const loadThread = useCallback(async (opts = {}) => {
        if (!opts.silent) setLoading(true);
        try {
            const { data } = await api.get("/patient/clinic-chat/thread");
            setClinic(data.clinic_doctor);
            setThread(data.messages || []);
        } catch (err) {
            if (!opts.silent) toast.error(err.response?.data?.detail || "Could not load chat");
        } finally {
            if (!opts.silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        api.get("/auth/me").then((r) => setPatientId(r.data?.id)).catch(() => {});
    }, []);

    useEffect(() => { loadThread(); }, [loadThread]);

    // Mark-read once on first load + auto-scroll on new messages.
    useEffect(() => {
        if (!patientId || loading || thread.length === 0) return;
        api.post(`/patients/${patientId}/messages/mark-read`).catch(() => {});
    }, [patientId, loading, thread.length]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [thread.length]);

    // Polling
    useEffect(() => {
        const tick = () => { if (!document.hidden) loadThread({ silent: true }); };
        pollTimerRef.current = setInterval(tick, POLL_MS);
        return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
    }, [loadThread]);

    // Attachments upload pipeline (unchanged from prior impl)
    const onFilesPicked = useCallback(async (files) => {
        if (!patientId) return;
        const list = Array.from(files || []);
        if (list.length === 0) return;
        for (const file of list) {
            setUploadingCount((n) => n + 1);
            try {
                const form = new FormData();
                form.append("file", file);
                const { data } = await api.post(
                    `/patients/${patientId}/chat-upload`, form,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
                setPendingAttachments((arr) => [...arr, {
                    id: data.id, name: data.name,
                    content_type: data.content_type, size: data.size,
                    is_image: (data.content_type || "").startsWith("image/"),
                }]);
            } catch (err) {
                toast.error(err.response?.data?.detail || `Couldn't upload ${file.name}`);
            } finally {
                setUploadingCount((n) => n - 1);
            }
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [patientId]);

    const sendMessage = async () => {
        const text = draft.trim();
        const attachmentIds = pendingAttachments.map((a) => a.id);
        if ((!text && attachmentIds.length === 0) || sending || !patientId) return;
        setSending(true);
        setShowQuickReplies(false);
        const optId = `opt-${Date.now()}`;
        const optimistic = {
            id: optId, author_name: "You", author_role: "patient",
            text, attachments: pendingAttachments, read_by: [],
            created_at: new Date().toISOString(), _pending: true,
        };
        setThread((m) => [...m, optimistic]);
        setDraft("");
        setPendingAttachments([]);
        try {
            const { data } = await api.post(`/patients/${patientId}/messages`, {
                text, attachment_file_ids: attachmentIds,
            });
            setThread((m) => m.map((x) => (x.id === optId ? data : x)));
            // Faster poll for 30s to catch the doctor's reply.
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            let bursts = 0;
            const fast = setInterval(() => {
                bursts += 1;
                if (bursts > 6) {
                    clearInterval(fast);
                    pollTimerRef.current = setInterval(
                        () => !document.hidden && loadThread({ silent: true }),
                        POLL_MS,
                    );
                    return;
                }
                if (!document.hidden) loadThread({ silent: true });
            }, FAST_POLL_MS);
            pollTimerRef.current = fast;
        } catch (err) {
            setThread((m) => m.filter((x) => x.id !== optId));
            setDraft(text);
            setPendingAttachments(optimistic.attachments || []);
            toast.error(err.response?.data?.detail || "Couldn't send message");
        } finally {
            setSending(false);
        }
    };

    // Group messages by day (for date dividers) — memoised so we re-build
    // only when the thread itself changes length/order.
    const grouped = useMemo(() => {
        const out = [];
        let currentKey = null;
        let bucket = null;
        for (let i = 0; i < thread.length; i += 1) {
            const m = thread[i];
            const k = dayKey(m.created_at);
            if (k !== currentKey) {
                bucket = { key: k, label: fmtDateDivider(m.created_at), items: [] };
                out.push(bucket);
                currentKey = k;
            }
            // Decide whether to render the avatar for this message — only
            // the first message in a doctor-sequence shows it.
            const prev = thread[i - 1];
            const sameAuthor = prev && prev.author_role === m.author_role;
            bucket.items.push({ msg: m, showAvatar: !sameAuthor });
        }
        return out;
    }, [thread]);

    if (loading && thread.length === 0) {
        return (
            <div className="text-center text-muted-foreground py-16 text-sm">
                <Spinner size={20} className="mx-auto mb-2 animate-spin" />
                Loading…
            </div>
        );
    }

    // No clinic mapping yet — patient hasn't been assigned to a case.
    if (!clinic) {
        return (
            <div className="max-w-md mx-auto py-16 text-center" data-testid="patient-clinic-chat-empty">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary mb-4">
                    <ChatsCircle size={26} weight="duotone" className="text-muted-foreground" />
                </div>
                <h2 className="font-display text-xl font-bold">Messaging unlocks after your first visit</h2>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    Once your doctor fits your aligners and activates your treatment, you can message them here for wear questions, photo updates &amp; rescheduling.
                </p>
            </div>
        );
    }

    return (
        <div
            className="flex flex-col h-[calc(100vh-6rem)] -mx-2 sm:mx-0"
            data-testid="patient-clinic-chat"
        >
            {/* ─────────── Top bar ─────────── */}
            <ChatTopBar
                clinic={clinic}
                onAppointments={() => navigate("/patient/appointments")}
                onRefresh={() => loadThread()}
            />

            {/* ─────────── Chat layout (thread + context) ─────────── */}
            <div className="flex flex-1 overflow-hidden">
                {/* THREAD COLUMN */}
                <div className="flex-1 flex flex-col overflow-hidden border-r border-border bg-card">
                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto px-4 sm:px-6 py-4"
                        data-testid="patient-clinic-chat-thread"
                    >
                        {grouped.length === 0 ? (
                            <div className="text-center text-muted-foreground text-xs py-12">
                                <ChatsCircle size={22} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60" />
                                Say hi to your clinic — they&apos;ll see your message and reply soon.
                            </div>
                        ) : (
                            grouped.map((g) => (
                                <React.Fragment key={g.key}>
                                    <DateDivider label={g.label} />
                                    {g.items.map(({ msg, showAvatar }) => (
                                        <MessageBubble key={msg.id} msg={msg} showAvatar={showAvatar} clinicName={clinic.name} clinicImage={clinic.image} />
                                    ))}
                                </React.Fragment>
                            ))
                        )}
                    </div>

                    {/* Composer */}
                    <Composer
                        draft={draft}
                        setDraft={setDraft}
                        onSend={sendMessage}
                        sending={sending}
                        pendingAttachments={pendingAttachments}
                        setPendingAttachments={setPendingAttachments}
                        onFilesPicked={onFilesPicked}
                        uploadingCount={uploadingCount}
                        fileInputRef={fileInputRef}
                        showQuickReplies={showQuickReplies}
                        onQuickReply={(t) => {
                            setDraft(t);
                            setShowQuickReplies(false);
                        }}
                        doctorName={clinic.name}
                    />
                </div>

                {/* CONTEXT PANEL — hidden on small screens */}
                <ContextPanel clinic={clinic} navigate={navigate} />
            </div>
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────
// ChatTopBar — doctor identity strip with online ring + actions.
// ────────────────────────────────────────────────────────────────────
function ChatTopBar({ clinic, onAppointments, onRefresh }) {
    return (
        <div
            className="flex items-center justify-between px-4 sm:px-6 py-2.5 bg-card border-b border-border shrink-0"
            data-testid="chat-topbar"
        >
            <div className="flex items-center gap-3 min-w-0">
                <div className="relative shrink-0">
                    <DoctorAvatar
                        src={clinic.image}
                        name={clinic.name}
                        size={36}
                        data-testid="chat-topbar-avatar"
                    />
                    {/* Live online dot — static visual cue; we don't run
                        real presence yet, so just mark them available. */}
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary border-2 border-card" />
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-medium truncate" data-testid="patient-clinic-chat-doctor-name">
                        {clinic.name}
                    </div>
                    <div className="text-[11px] text-primary flex items-center gap-1.5 truncate">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                        Online · <span className="text-muted-foreground">Typically replies within 4 hrs</span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-1.5">
                <IconBtn title="Appointments" onClick={onAppointments} data-testid="chat-appointments-btn">
                    <CalendarPlus size={15} />
                </IconBtn>
                <IconBtn title="Refresh thread" onClick={onRefresh} data-testid="chat-refresh-btn">
                    <ArrowsClockwise size={15} />
                </IconBtn>
            </div>
        </div>
    );
}

const IconBtn = ({ children, ...rest }) => (
    <button
        className="w-8 h-8 rounded-md border border-border bg-card hover:bg-secondary text-muted-foreground hover:text-foreground transition flex items-center justify-center"
        {...rest}
    >
        {children}
    </button>
);


// ────────────────────────────────────────────────────────────────────
// DateDivider — horizontal line with the day label centered.
// ────────────────────────────────────────────────────────────────────
function DateDivider({ label }) {
    return (
        <div className="flex items-center gap-3 my-3">
            <div className="flex-1 h-px bg-border" />
            <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground whitespace-nowrap">
                {label}
            </div>
            <div className="flex-1 h-px bg-border" />
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────
// MessageBubble — left for doctor / clinic_staff, right for patient.
// `showAvatar` controls whether the doctor avatar is rendered (only on
// the first message of a same-author run).
// ────────────────────────────────────────────────────────────────────
function MessageBubble({ msg, showAvatar, clinicName, clinicImage }) {
    const mine = msg.author_role === "patient";
    const attachments = msg.attachments || [];
    const recipientReads = (msg.read_by || []).filter((r) => r.user_id !== msg.author_id).length;
    const showCheck = mine && !msg._pending;
    const isStaff = msg.author_role === "clinic_staff";

    return (
        <div
            className={`flex items-end gap-2 mb-1.5 ${mine ? "justify-end" : "justify-start"}`}
            data-testid={`patient-chat-msg-${msg.id}`}
        >
            {!mine && (
                <div className={`shrink-0 mb-1 ${showAvatar ? "" : "invisible"}`}>
                    <DoctorAvatar
                        src={clinicImage}
                        name={msg.author_name || clinicName}
                        size={28}
                        initialsClassName="bg-indigo-50 border border-indigo-200 text-indigo-700 text-[10px]"
                    />
                </div>
            )}

            <div className={`max-w-[78%] min-w-0 flex flex-col ${mine ? "items-end" : "items-start"}`}>
                {!mine && showAvatar && isStaff && (
                    <div className="text-[10px] text-muted-foreground mb-1">
                        {msg.author_name} · Clinic
                    </div>
                )}

                <div
                    className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed break-words ${
                        mine
                            ? "bg-primary text-primary-foreground rounded-br-md"
                            : "bg-secondary/60 border border-border text-foreground rounded-bl-md"
                    } ${msg._pending ? "opacity-60" : ""}`}
                >
                    {attachments.length > 0 && (
                        <div className={`space-y-1.5 ${msg.text ? "mb-2" : ""}`}>
                            {attachments.map((a) => (
                                <AttachmentChip key={a.id} att={a} mine={mine} />
                            ))}
                        </div>
                    )}
                    {msg.text && <div className="whitespace-pre-wrap">{msg.text}</div>}
                </div>

                <div className={`text-[10px] mt-1 flex items-center gap-1 text-muted-foreground ${mine ? "" : ""}`}>
                    <span>{fmtTime(msg.created_at)}{msg._pending && " · sending…"}</span>
                    {showCheck && (
                        recipientReads > 0
                            ? <Checks size={12} weight="bold" className="text-primary" title="Read by your clinic" />
                            : <Check size={12} weight="bold" className="text-muted-foreground/70" title="Sent" />
                    )}
                </div>
            </div>
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────
// AttachmentChip — image bubbles render inline, files render as a chip
// with name + size. Click to download.
// ────────────────────────────────────────────────────────────────────
function AttachmentChip({ att, mine }) {
    const isImage = (att.content_type || "").startsWith("image/")
        || /\.(png|jpg|jpeg|gif|webp|heic)$/i.test(att.name || "");
    const [blobUrl, setBlobUrl] = useState(null);

    useEffect(() => {
        if (!isImage || !att.id) return undefined;
        let revoked = false;
        let url = null;
        api.get(`/patient-chat-files/${att.id}`, { responseType: "blob" })
            .then((r) => {
                if (revoked) return;
                url = URL.createObjectURL(r.data);
                setBlobUrl(url);
            })
            .catch(() => {});
        return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
    }, [att.id, isImage]);

    const downloadFile = async () => {
        try {
            const r = await api.get(`/patient-chat-files/${att.id}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = att.name || "download";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch {
            toast.error("Couldn't download attachment");
        }
    };

    if (isImage) {
        return (
            <button
                type="button"
                onClick={downloadFile}
                className="block max-w-[220px] rounded-lg overflow-hidden border border-black/10 bg-black/5 cursor-zoom-in"
                title={att.name}
            >
                {blobUrl
                    ? <img src={blobUrl} alt={att.name} className="block max-h-[180px] w-auto" />
                    : (
                        <div className="w-[200px] h-[140px] flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-primary/10 to-secondary">
                            <Camera size={22} weight="duotone" className="text-primary" />
                            <span className="text-[11px] font-medium text-primary/80">{att.name || "Photo"}</span>
                        </div>
                    )}
            </button>
        );
    }

    const cls = mine
        ? "bg-white/15 hover:bg-white/25 border-white/20 text-white"
        : "bg-card hover:bg-secondary border-border text-foreground";

    return (
        <button
            type="button"
            onClick={downloadFile}
            className={`flex items-center gap-2.5 max-w-[260px] px-3 py-2 rounded-lg border text-left transition ${cls}`}
        >
            <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${
                mine ? "bg-white/10" : "bg-primary/10"
            }`}>
                <FileText size={17} weight="duotone" className={mine ? "text-white" : "text-primary"} />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{att.name || "File"}</div>
                <div className={`text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                    {fmtSize(att.size)} · Tap to open
                </div>
            </div>
        </button>
    );
}


// ────────────────────────────────────────────────────────────────────
// Composer — quick replies + auto-resize textarea + attachment + send.
// ────────────────────────────────────────────────────────────────────
function Composer({
    draft, setDraft, onSend, sending,
    pendingAttachments, setPendingAttachments,
    onFilesPicked, uploadingCount, fileInputRef,
    showQuickReplies, onQuickReply, doctorName,
}) {
    const textareaRef = useRef(null);

    // Auto-grow textarea up to ~120px.
    useEffect(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        ta.style.height = "auto";
        ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
    }, [draft]);

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSend();
        }
    };

    const remove = (id) => setPendingAttachments((arr) => arr.filter((a) => a.id !== id));
    const canSend = !sending && uploadingCount === 0 && (draft.trim().length > 0 || pendingAttachments.length > 0);

    return (
        <div className="border-t border-border bg-card px-3 sm:px-4 py-3 shrink-0">
            {/* Quick reply chips — hidden after first send */}
            {showQuickReplies && (
                <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-none">
                    {QUICK_REPLIES.map((q) => (
                        <button
                            key={q}
                            onClick={() => onQuickReply(q)}
                            className="whitespace-nowrap px-3 py-1 rounded-full border border-border text-xs text-muted-foreground hover:border-primary/40 hover:bg-primary/10 hover:text-primary transition shrink-0"
                            data-testid={`quick-reply-${q.slice(0, 12)}`}
                        >
                            {q}
                        </button>
                    ))}
                </div>
            )}

            {/* Pending attachments preview */}
            {(pendingAttachments.length > 0 || uploadingCount > 0) && (
                <div className="mb-2 flex flex-wrap gap-2">
                    {pendingAttachments.map((a) => (
                        <div
                            key={a.id}
                            className="relative inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-secondary border border-border text-[11px]"
                        >
                            {a.is_image
                                ? <ImageIcon size={13} weight="duotone" className="text-primary shrink-0" />
                                : <FileText size={13} weight="duotone" className="text-muted-foreground shrink-0" />}
                            <span className="font-medium truncate max-w-[140px]">{a.name}</span>
                            <button
                                type="button"
                                onClick={() => remove(a.id)}
                                className="w-5 h-5 rounded-full hover:bg-rose-100 hover:text-rose-700 flex items-center justify-center transition"
                                aria-label="Remove attachment"
                            >
                                <X size={11} weight="bold" />
                            </button>
                        </div>
                    ))}
                    {uploadingCount > 0 && (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary/70 border border-border text-[11px] text-muted-foreground">
                            <Spinner size={12} className="animate-spin" />
                            Uploading {uploadingCount} file{uploadingCount === 1 ? "" : "s"}…
                        </div>
                    )}
                </div>
            )}

            {/* Composer input row */}
            <div className="flex items-end gap-2">
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => onFilesPicked(e.target.files)}
                    className="hidden"
                    multiple
                    accept="image/*,application/pdf"
                    data-testid="patient-clinic-chat-file-input"
                />
                <div className="flex-1 flex items-end gap-2 px-3 py-2 rounded-xl border border-border bg-secondary/40 focus-within:border-primary/40 focus-within:bg-card transition">
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={onKey}
                        placeholder={`Message ${doctorName?.split(" ")[0] || "your clinic"}…`}
                        className="flex-1 bg-transparent outline-none text-sm resize-none min-h-[20px] max-h-[120px] leading-relaxed"
                        data-testid="patient-clinic-chat-composer"
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        title="Attach a file or photo"
                        className="text-muted-foreground hover:text-primary transition shrink-0 pb-0.5"
                        data-testid="patient-clinic-chat-attach"
                    >
                        <Paperclip size={17} />
                    </button>
                </div>
                <button
                    type="button"
                    onClick={onSend}
                    disabled={!canSend}
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition ${
                        canSend
                            ? "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95"
                            : "bg-border text-muted-foreground cursor-not-allowed"
                    }`}
                    data-testid="patient-clinic-chat-send"
                    aria-label="Send message"
                >
                    {sending ? <Spinner size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}
                </button>
            </div>

            {/* Privacy footnote */}
            <div className="text-[10px] text-muted-foreground mt-2 text-center inline-flex items-center justify-center gap-1 w-full">
                <Lock size={11} weight="fill" />
                Messages are private and visible only to you and your clinic. Press Enter to send · Shift + Enter for a new line.
            </div>
        </div>
    );
}


// ────────────────────────────────────────────────────────────────────
// ContextPanel — right rail. Pulls case + appt + photo + wear data
// independently so a slow endpoint doesn't block the thread.
// ────────────────────────────────────────────────────────────────────
function ContextPanel({ clinic, navigate }) {
    const [nextAppt, setNextAppt] = useState(null);
    const [progress, setProgress] = useState(null);
    const [activeCase, setActiveCase] = useState(null);
    const [photos, setPhotos] = useState([]);
    const [photoCacheBust] = useState(Date.now());

    useEffect(() => {
        api.get("/appointments/my").then((r) => {
            const list = (r.data || [])
                .filter((a) => !["cancelled", "completed", "rescheduled", "no_show"].includes(a.status))
                .filter((a) => {
                    try {
                        return new Date(`${a.date}T${a.time || "00:00"}:00`).getTime() >= Date.now() - 4 * 3600_000;
                    } catch { return true; }
                })
                .sort((a, b) => `${a.date} ${a.time || ""}`.localeCompare(`${b.date} ${b.time || ""}`));
            setNextAppt(list[0] || null);
        }).catch(() => {});

        api.get("/patient/me/aligner-progress").then((r) => setProgress(r.data)).catch(() => {});

        api.get("/cases/mine").then((r) => {
            const cs = (r.data || []).find((c) => !["draft", "cancelled"].includes(c.status));
            setActiveCase(cs || null);
        }).catch(() => {});
    }, []);

    // Pull recent photos once activeCase is resolved.
    useEffect(() => {
        if (!activeCase?.id) return;
        api.get(`/patient/me/case/${activeCase.id}/photos`)
            .then((r) => setPhotos((r.data || []).slice(0, 5)))
            .catch(() => {});
    }, [activeCase?.id]);

    const wearToday = progress?.wear?.today_hours;
    const wearTarget = progress?.wear?.target_hours || 22;
    const wearPct = wearToday != null ? Math.min(100, Math.round((wearToday / wearTarget) * 100)) : null;

    const stageNum = progress?.stage?.current;
    const stageTotal = progress?.stage?.total;
    const stagePct = stageNum && stageTotal ? Math.round((stageNum / stageTotal) * 100) : null;

    const phaseLabel = (() => {
        const st = activeCase?.status;
        if (st === "completed") return "Retention Phase";
        if (st === "retention") return "Retention Phase";
        if (st && stageNum && stageTotal && stageNum >= stageTotal) return "Final aligner";
        if (st === "in_treatment" || st === "treatment_started") return "In treatment";
        return "Pre-treatment";
    })();

    return (
        <aside
            className="hidden lg:flex w-64 shrink-0 bg-card overflow-y-auto p-4 flex-col gap-3"
            data-testid="patient-clinic-chat-context-panel"
        >
            {/* Doctor card */}
            <div className="bg-secondary/60 rounded-xl p-3 text-center">
                <div className="mx-auto mb-2 w-fit">
                    <DoctorAvatar
                        src={clinic.image}
                        name={clinic.name}
                        size={48}
                        ringClassName="ring-2 ring-indigo-200"
                    />
                </div>
                <div className="text-sm font-semibold">{clinic.name}</div>
                <div className="text-[11px] text-muted-foreground mb-2">
                    {clinic.clinic_name || "Orthodontist"}
                </div>
                <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-primary bg-primary/10 rounded-full px-2.5 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                    Online now
                </div>
            </div>

            {/* Response time badge */}
            <div className="flex items-start gap-2 bg-primary/10 rounded-md px-3 py-2">
                <Clock size={15} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                <div className="text-[11px] text-primary leading-relaxed">
                    <span className="font-semibold">Typically replies in 4 hrs</span><br />
                    Monday – Friday · 9 am – 6 pm
                </div>
            </div>

            <Divider />

            {/* Next appointment */}
            <CtxSection icon={CalendarBlank} title="Next appointment">
                {nextAppt ? (
                    <CtxCard onClick={() => navigate("/patient/appointments")} testid="ctx-next-appt">
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                                <CalendarBlank size={14} className="text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium truncate">
                                    {(nextAppt.appointment_type || nextAppt.type || "Consultation").replace(/_/g, " ")}
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                    {fmtDateShort(nextAppt.date)} · {fmtAppTime(nextAppt.time)}
                                </div>
                            </div>
                        </div>
                        <div className="text-[11px] text-primary font-medium mt-1.5">
                            {relDaysLabel(nextAppt.date)} →
                        </div>
                    </CtxCard>
                ) : (
                    <CtxCard onClick={() => navigate("/patient/appointments")} testid="ctx-no-appt">
                        <div className="text-xs text-muted-foreground">No upcoming visit</div>
                        <div className="text-[11px] text-primary font-medium mt-1">Book an appointment →</div>
                    </CtxCard>
                )}
            </CtxSection>

            {/* Treatment phase */}
            {activeCase && (
                <CtxSection icon={Tooth} title="Treatment">
                    <CtxCard onClick={() => navigate("/patient/my-case")} testid="ctx-treatment">
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-xs font-medium">{phaseLabel}</div>
                            <span className="text-[10px] font-semibold bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                                Active
                            </span>
                        </div>
                        {stagePct != null && (
                            <>
                                <div className="bg-secondary rounded-full h-1 overflow-hidden mb-1">
                                    <div className="h-full bg-primary rounded-full" style={{ width: `${stagePct}%` }} />
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                    {stageNum} / {stageTotal} aligners
                                    {stageNum >= stageTotal && " ✓"}
                                </div>
                            </>
                        )}
                    </CtxCard>
                </CtxSection>
            )}

            {/* Recent photos */}
            {activeCase && photos.length > 0 && (
                <CtxSection icon={Camera} title="Recent photos">
                    <div className="flex gap-1.5 overflow-x-auto scrollbar-none" data-testid="ctx-recent-photos">
                        {photos.map((p) => (
                            <PhotoThumb key={p.id} photo={p} onOpen={() => navigate("/patient/my-case")} bust={photoCacheBust} />
                        ))}
                        <button
                            type="button"
                            onClick={() => navigate("/patient")}
                            className="w-12 h-12 rounded-md bg-secondary border-2 border-dashed border-border flex items-center justify-center shrink-0 hover:border-primary/40 transition"
                            aria-label="Upload photo"
                            data-testid="ctx-upload-photo"
                        >
                            <Plus size={14} className="text-muted-foreground" />
                        </button>
                    </div>
                </CtxSection>
            )}

            {/* Today's wear */}
            {wearPct != null && (
                <CtxSection icon={Clock} title="Today's wear">
                    <CtxCard onClick={() => navigate("/patient/my-case")} testid="ctx-wear">
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="text-xs font-medium">{wearToday?.toFixed(1)} hrs logged</div>
                            <span className="text-[10px] text-primary font-medium">{wearPct}%</span>
                        </div>
                        <div className="bg-secondary rounded-full h-1 overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${wearPct}%` }} />
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1">Target: {wearTarget} hrs / day</div>
                    </CtxCard>
                </CtxSection>
            )}

            {/* Quick actions */}
            <CtxSection icon={Lightning} title="Quick actions">
                <div className="flex flex-col gap-1.5">
                    <QuickAction icon={CalendarPlus} label="Book an appointment" onClick={() => navigate("/patient/appointments")} testid="quick-book-appt" />
                    <QuickAction icon={Camera} label="Upload check-in photos" onClick={() => navigate("/patient")} testid="quick-upload-photos" />
                    <QuickAction icon={Tooth} label="View my journey" onClick={() => navigate("/patient/my-case")} testid="quick-view-journey" />
                </div>
            </CtxSection>
        </aside>
    );
}


// ────────────────────────────────────────────────────────────────────
// Context panel building blocks
// ────────────────────────────────────────────────────────────────────
const Divider = () => <hr className="border-t border-border" />;

const CtxSection = ({ icon: Icon, title, children }) => (
    <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <Icon size={12} /> {title}
        </div>
        {children}
    </div>
);

const CtxCard = ({ children, onClick, testid }) => (
    <button
        type="button"
        onClick={onClick}
        className="w-full text-left border border-border rounded-xl p-2.5 hover:bg-secondary/60 hover:border-primary/30 transition"
        data-testid={testid}
    >
        {children}
    </button>
);

const QuickAction = ({ icon: Icon, label, onClick, testid }) => (
    <button
        type="button"
        onClick={onClick}
        className="flex items-center gap-2 border border-border rounded-xl px-3 py-2 hover:bg-secondary/60 hover:border-primary/30 transition text-left"
        data-testid={testid}
    >
        <Icon size={15} className="text-primary shrink-0" />
        <span className="text-xs">{label}</span>
    </button>
);


// ────────────────────────────────────────────────────────────────────
// PhotoThumb — tries to load the patient photo via the existing
// /patient/me/case/{id}/photos feed (inline data URLs). Falls back to
// a tooth emoji placeholder.
// ────────────────────────────────────────────────────────────────────
function PhotoThumb({ photo, onOpen }) {
    const src = photo.data_url || photo.url || photo.thumbnail_url;
    return (
        <button
            type="button"
            onClick={onOpen}
            className="w-12 h-12 rounded-md bg-primary/10 border border-border overflow-hidden flex items-center justify-center text-base shrink-0 hover:border-primary/40 transition"
            aria-label="Open photo"
        >
            {src
                ? <img src={src} alt="" className="w-full h-full object-cover" />
                : <Camera size={16} className="text-primary" />}
        </button>
    );
}


// ────────────────────────────────────────────────────────────────────
// Small formatters specific to the context panel.
// ────────────────────────────────────────────────────────────────────
const fmtDateShort = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
            weekday: "short", day: "2-digit", month: "short",
        });
    } catch { return iso; }
};

const fmtAppTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    if (Number.isNaN(h)) return t;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m || 0).padStart(2, "0")} ${period}`;
};

const relDaysLabel = (iso) => {
    if (!iso) return "—";
    try {
        const target = new Date(`${iso}T00:00:00`).getTime();
        const days = Math.round((target - Date.now()) / 86400000);
        if (days < 0) return "Today";
        if (days === 0) return "Today";
        if (days === 1) return "Tomorrow";
        return `In ${days} days`;
    } catch { return ""; }
};
